import { AppDispatch } from "../../store/store";
import axiosInstance from "../../utility/axios";
import { getErrorMessage } from "../apiError";
import {
  disableMarketerLoading,
  createMarketer,
  editMarketer,
  setLoadingMarketerId,
  setMarketer,
  setMarketerLoading,
  setMarketers,
  updateMarketerStatus,
  deleteMarketer,
} from "../../store/slices/marketers";
import {
  IMarketerCreate,
  IMarketerCreateResponse,
  IMarketerResponse,
  IMarketersResponse,
  IToggleMarketerStatusResponse,
} from "../../store/types/marketers";
import { toast } from "sonner";
import { clearMarketer } from "../../store/slices/marketers";

const marketerUrl = "/api/v1/admin";

export const get_marketers = async (
  dispatch: AppDispatch,
  offset: number = 0,
  limit: number = 20,
) => {
  dispatch(setMarketerLoading());

  try {
    const response = await axiosInstance.get<IMarketersResponse>(
      `${marketerUrl}/marketers`,
      { params: { offset, limit } },
    );

    dispatch(setMarketers(response.data));
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to load marketers"));
  } finally {
    dispatch(disableMarketerLoading());
  }
};

export const get_marketer = async (
  dispatch: AppDispatch,
  id: string,
): Promise<boolean> => {
  dispatch(setMarketerLoading());

  try {
    const response = await axiosInstance.get<IMarketerResponse>(
      `${marketerUrl}/marketers/${id}`,
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to load marketer");
    }
    dispatch(setMarketer(response.data.data));
    return true;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to load marketer details"));
    return false;
  } finally {
    dispatch(disableMarketerLoading());
  }
};

export const clear_marketer = (dispatch: AppDispatch) => {
  dispatch(clearMarketer());
};

export const toggle_marketer_status = async (
  dispatch: AppDispatch,
  id: number,
  isActive: boolean,
) => {
  dispatch(setLoadingMarketerId(id));

  try {
    const response = await axiosInstance.patch<IToggleMarketerStatusResponse>(
      `${marketerUrl}/marketers/${id}/status`,
      {
        is_active: isActive,
      },
    );

    if (!response.data.success) {
      throw new Error(
        response.data.message || "Failed to update marketer status",
      );
    }
    dispatch(
      updateMarketerStatus({
        id: response.data.data.id,
        is_active: response.data.data.is_active,
      }),
    );
    toast.success(response.data.message || "Marketer status updated");
    return response.data;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to update marketer status"));
  } finally {
    dispatch(setLoadingMarketerId(null));
  }
};

export const create_marketer = async (
  dispatch: AppDispatch,
  marketerData: IMarketerCreate,
): Promise<boolean> => {
  dispatch(setMarketerLoading());
  try {
    const response = await axiosInstance.post<IMarketerCreateResponse>(
      `${marketerUrl}/marketers`,
      marketerData,
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to create marketer");
    }
    dispatch(createMarketer(response.data.data));
    toast.success(response.data.message || "Marketer created successfully");
    return true;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to create marketer"));
    return false;
  } finally {
    dispatch(disableMarketerLoading());
  }
};

export const update_marketer = async (
  dispatch: AppDispatch,
  id: number,
  marketerData: Partial<IMarketerCreate>,
): Promise<boolean> => {
  dispatch(setMarketerLoading());
  try {
    const response = await axiosInstance.patch<IMarketerCreateResponse>(
      `${marketerUrl}/marketers/${id}`,
      marketerData,
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to update marketer");
    }
    dispatch(editMarketer(response.data.data));
    toast.success(response.data.message || "Marketer updated successfully");
    return true;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to update marketer"));
    return false;
  } finally {
    dispatch(disableMarketerLoading());
  }
};

export const delete_marketer = async (dispatch: AppDispatch, id: number) => {
  dispatch(setLoadingMarketerId(id));
  try {
    const response = await axiosInstance.delete<IMarketerCreateResponse>(
      `${marketerUrl}/marketers/${id}`,
    );
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to delete marketer");
    }
    dispatch(deleteMarketer(id));
    toast.success(response.data.message || "Marketer deleted successfully");
    return true;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to delete marketer"));
    return false;
  } finally {
    dispatch(setLoadingMarketerId(null));
  }
};
